/* File: notifications.jsx
 * Path: ananunadm/src/components/main/notifications.jsx
*/
import moment from "moment";
import { useEffect, useMemo, useRef } from "react";
import { useSocket } from "../../helpers/socket";
import { notificationIdentity } from "../../helpers/notificationIdentity";
import { useLang } from "../../helpers/language";
import { useUser } from "../../helpers/userContext";
import parse from "html-react-parser";
import { Text, Modal, ScrollArea, Indicator, Badge, Box, Button, Group, useMantineTheme } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import { T } from "../../helpers/translator";
import { getUserImageSrc } from "../../helpers/uploadImage";
import { useHistory } from "react-router-dom";
import { IconBell } from "@tabler/icons-react";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import { MRT_Localization_EN } from "mantine-react-table/locales/en";
import { MRT_Localization_TR } from "mantine-react-table/locales/tr";
import { MRT_Localization_RU } from "mantine-react-table/locales/ru";
import { MRT_Localization_AR } from "mantine-react-table/locales/ar";
import { MRT_Localization_FA } from "mantine-react-table/locales/fa";
import { MRT_Localization_FR } from "mantine-react-table/locales/fr";
import { MRT_Localization_ES } from "mantine-react-table/locales/es";
import { MRT_Localization_PT } from "mantine-react-table/locales/pt";
import { MRT_Localization_ZH_HANS } from "mantine-react-table/locales/zh-Hans";
import classes from "./notifications.module.css";

// Keep the modal header outside the table's scroll container on phones.
function NotificationPanelScroll({ children }) {
	return <div className={classes.panelLayout}>{children}</div>;
}

const MRT_LOCALIZATION_BY_LANG = {
	en: MRT_Localization_EN,
	tr: MRT_Localization_TR,
	ru: MRT_Localization_RU,
	ar: MRT_Localization_AR,
	fa: MRT_Localization_FA,
	fr: MRT_Localization_FR,
	es: MRT_Localization_ES,
	pt: MRT_Localization_PT,
	zh: MRT_Localization_ZH_HANS,
	hy: MRT_Localization_EN,
	he: MRT_Localization_EN
};

const LEGACY_TODO_EVENT_LABELS = {
	"Task created": {
		ru: "Задача создана", hy: "Առաջադրանքը ստեղծվել է", tr: "Görev oluşturuldu",
		ar: "تم إنشاء المهمة", zh: "任务已创建", fa: "وظیفه ایجاد شد",
		he: "המשימה נוצרה", fr: "Tâche créée", es: "Tarea creada", pt: "Tarefa criada"
	},
	"Task moved": {
		ru: "Задача перемещена", hy: "Առաջադրանքը տեղափոխվել է", tr: "Görev taşındı",
		ar: "تم نقل المهمة", zh: "任务已移动", fa: "وظیفه منتقل شد",
		he: "המשימה הועברה", fr: "Tâche déplacée", es: "Tarea movida", pt: "Tarefa movida"
	},
	"Comment added": {
		ru: "Комментарий добавлен", hy: "Մեկնաբանություն է ավելացվել", tr: "Yorum eklendi",
		ar: "تمت إضافة تعليق", zh: "已添加评论", fa: "نظر اضافه شد",
		he: "נוספה תגובה", fr: "Commentaire ajouté", es: "Comentario añadido", pt: "Comentário adicionado"
	},
	"Attachment added": {
		ru: "Вложение добавлено", hy: "Կցորդ է ավելացվել", tr: "Ek eklendi",
		ar: "تمت إضافة مرفق", zh: "已添加附件", fa: "پیوست اضافه شد",
		he: "נוסף קובץ מצורף", fr: "Pièce jointe ajoutée", es: "Archivo adjunto añadido", pt: "Anexo adicionado"
	},
	"Task closed": {
		ru: "Задача закрыта", hy: "Առաջադրանքը փակվել է", tr: "Görev kapatıldı",
		ar: "تم إغلاق المهمة", zh: "任务已关闭", fa: "وظیفه بسته شد",
		he: "המשימה נסגרה", fr: "Tâche clôturée", es: "Tarea cerrada", pt: "Tarefa encerrada"
	}
};

const LEGACY_EMPTY_COLUMN_TITLES = {
	ru: "Пустой столбец TODO требует внимания",
	hy: "TODO-ի դատարկ սյունակը ուշադրություն է պահանջում",
	tr: "Boş TODO sütunuyla ilgilenilmesi gerekiyor",
	ar: "عمود TODO الفارغ يحتاج إلى اهتمام",
	zh: "空的 TODO 列需要处理",
	fa: "ستون خالی TODO نیاز به رسیدگی دارد",
	he: "עמודת TODO הריקה דורשת טיפול",
	fr: "La colonne TODO vide nécessite votre attention",
	es: "La columna TODO vacía requiere atención",
	pt: "A coluna TODO vazia precisa de atenção"
};

function localizeLegacyTodoText(notification, value, lang) {
	if (
		lang === "en" ||
		typeof value !== "string" ||
		!String(notification?.route || "").replace(/^\/?/, "/").startsWith("/todo/")
	)
	{
		return value;
	}

	if (value === "Empty TODO column needs attention")
	{
		return LEGACY_EMPTY_COLUMN_TITLES[lang] || value;
	}

	const emptyColumnMatch = value.match(
		/^(.+?) on (.+?) has been empty for (\d+) minutes?\. Please take action\.$/
	);
	if (emptyColumnMatch)
	{
		const [, columnName, boardName, minutes] = emptyColumnMatch;
		const descriptions = {
			ru: `Столбец «${columnName}» на доске «${boardName}» пуст уже ${minutes} мин. Примите меры.`,
			hy: `«${boardName}» տախտակի «${columnName}» սյունակը դատարկ է արդեն ${minutes} րոպե։ Խնդրում ենք միջոցներ ձեռնարկել։`,
			tr: `${boardName} panosundaki ${columnName} sütunu ${minutes} dakikadır boş. Lütfen gerekli işlemi yapın.`,
			ar: `عمود «${columnName}» في لوحة «${boardName}» فارغ منذ ${minutes} دقيقة. يرجى اتخاذ الإجراء اللازم.`,
			zh: `看板“${boardName}”中的“${columnName}”列已空置 ${minutes} 分钟。请及时处理。`,
			fa: `ستون «${columnName}» در برد «${boardName}» به مدت ${minutes} دقیقه خالی بوده است. لطفاً رسیدگی کنید.`,
			he: `העמודה „${columnName}” בלוח „${boardName}” ריקה כבר ${minutes} דקות. יש לטפל בכך.`,
			fr: `La colonne « ${columnName} » du tableau « ${boardName} » est vide depuis ${minutes} minute${minutes === "1" ? "" : "s"}. Veuillez intervenir.`,
			es: `La columna «${columnName}» del tablero «${boardName}» lleva ${minutes} minuto${minutes === "1" ? "" : "s"} vacía. Tome las medidas necesarias.`,
			pt: `A coluna “${columnName}” do quadro “${boardName}” está vazia há ${minutes} minuto${minutes === "1" ? "" : "s"}. Tome as medidas necessárias.`
		};
		return descriptions[lang] || value;
	}

	for (const [englishLabel, translations] of Object.entries(LEGACY_TODO_EVENT_LABELS))
	{
		if (value === englishLabel || value.startsWith(`${englishLabel}:`))
		{
			return value.replace(englishLabel, translations[lang] || englishLabel);
		}
	}
	return value;
}

function getDesktopNotificationApi() {
	if (typeof window === "undefined") return null;
	return window.apitDesktop && typeof window.apitDesktop.notify === "function"
		? window.apitDesktop
		: null;
}

function canUseBrowserNotifications() {
	return typeof window !== "undefined" && "Notification" in window;
}

function isReminderNotification(notification) {
	const route = typeof notification?.route === "string" ? notification.route : "";
	if (route === "calendar" || route.includes("/calendar")) return true;

	const title =
		notification?.name?.en ||
		Object.values(notification?.name || {}).find((value) => typeof value === "string" && value.trim()) ||
		"";

	return /^(Reminder:|Meeting:|Ticket deadline:)/i.test(String(title).trim());
}

export default function Notifications({ isMobile, compact }) {
	const { myNotifications, socket, setMyNotifications } = useSocket();
	const audio = useRef(null);
	const clearAudio = useRef(null);
	const shownNotificationIds = useRef(new Set());
	const notificationsHydrated = useRef(false);
	const { lang } = useLang();
	const history = useHistory();
	const { user } = useUser();
	const theme = useMantineTheme();
	const panelBackground = theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white;
	const [opened, OpenClose] = useDisclosure(false);

	const mrtLocalization = useMemo(
		() => MRT_LOCALIZATION_BY_LANG[lang] || MRT_Localization_EN,
		[lang]
	);

	// Set Locale Dynamically by lang
	moment.locale(lang === "hy" ? "hy-am" : lang); // lang could be "hy", "ru", "en", etc.

	const emitNotificationRead = (notificationId) => {
		if (!notificationId) return;
		socket.emit(
			"readNotification",
			JSON.stringify({
				userId: user._id,
				notificationId
			})
		);
	};

	const playClearNotificationSound = () => {
		if (audio.current)
		{
			audio.current.pause();
			audio.current.currentTime = 0;
		}
		if (!clearAudio.current) return;
		clearAudio.current.src = "/assets/clear_notifications.mp3";
		clearAudio.current.load();
		clearAudio.current.play().catch((err) => console.warn("Audio play blocked:", err));
	};

	function updateTabTitle(notifications) {
		if (notifications > 0)
		{
			document.title = `APIT - ${notifications} ${T("notifications", lang)}`;
		} else
		{
			document.title = "APIT";
		}
	}
	// Calculate unread notifications
	const unreadNotifications =
		myNotifications?.filter((notification) => !notification.read?.includes(user._id)) || [];
	const unreadCount = unreadNotifications.length;
	const unreadLabel = unreadCount > 99 ? "99+" : unreadCount;

	const openNotificationTarget = (notification) => {
		if (!notification) return;
		const link =
			typeof notification.route === "string" ? notification.route.trim() : "";
		if (!link) return;

		if (notification?._id)
		{
			emitNotificationRead(notification._id);
		}

		const normalizedLink = link.startsWith("/") ? link : `/${link}`;
		history.push(normalizedLink);
	};

	const showSystemNotification = async ({
		title,
		message,
		notificationKey,
		notification,
		autoCloseMs
	}) => {
		const desktopApi = getDesktopNotificationApi();

		if (desktopApi)
		{
			try
			{
				const senderImageUrl = notification?.createdByUser
					? getUserImageSrc(notification.createdByUser, { targetWidth: 64 })
					: "";
				const result = await desktopApi.notify({
					title,
					body: message,
					id: String(notificationKey || ""),
					route: notification?.route || "",
					senderInitial: title.charAt(0),
					senderUserId: notification?.createdByUser?._id || "",
					senderImageUrl,
					senderUploads: Array.isArray(notification?.createdByUser?.uploads)
						? notification.createdByUser.uploads
						: [],
				});

				if (result?.ok)
				{
					return;
				}
			} catch (error)
			{
				console.warn("Desktop notification bridge failed:", error);
			}
		}

		if (!canUseBrowserNotifications()) return;

		if (Notification.permission === "default")
		{
			try
			{
				await Notification.requestPermission();
			} catch (error)
			{
				console.warn("Notification permission request failed:", error);
			}
		}

		if (Notification.permission !== "granted") return;

		const desktopNotification = new Notification(title, {
			body: message,
			tag: notificationKey
		});

		desktopNotification.onclick = () => {
			window.focus();
			openNotificationTarget(notification);
			desktopNotification.close();
		};

		setTimeout(() => {
			desktopNotification.close();
		}, autoCloseMs);
	};

	useEffect(() => {
		if (!Array.isArray(myNotifications)) return;

		if (notificationsHydrated.current) return;

		myNotifications.forEach((notification) => {
			const key = notificationIdentity(notification);
			if (key != null)
			{
				shownNotificationIds.current.add(key);
			}
			// Also seed _id/ID directly so a same-message placeholder/persisted pair that later
			// swaps identity (see handleNewNotification in helpers/socket.js) still matches below.
			if (notification?._id != null) shownNotificationIds.current.add(notification._id);
			if (notification?.ID != null) shownNotificationIds.current.add(notification.ID);
		});

		notificationsHydrated.current = true;
	}, [myNotifications]);

	useEffect(() => {
		const desktopApi = getDesktopNotificationApi();
		if (!desktopApi || typeof desktopApi.onNotificationClick !== "function")
		{
			return undefined;
		}

		const unsubscribe = desktopApi.onNotificationClick(({ route }) => {
			if (!route) return;
			const normalizedLink = route.startsWith("/") ? route : `/${route}`;
			history.push(normalizedLink);
		});

		return () => {
			if (typeof unsubscribe === "function") unsubscribe();
		};
	}, [history]);

	useEffect(() => {
		const desktopApi = getDesktopNotificationApi();
		desktopApi?.setBadgeCount?.(unreadCount);
	}, [unreadCount]);

	useEffect(() => {
		if (unreadCount === 0)
		{
			document.title = "APIT";
			return undefined;
		}

		const interval = setInterval(() => {
			updateTabTitle(unreadCount);
			setTimeout(() => {
				updateTabTitle(0);
			}, 1500);
		}, 3000);

		return () => {
			document.title = "APIT Admin";
			clearInterval(interval);
		};
	}, [unreadCount, lang]);

	useEffect(() => {
		if (!myNotifications?.length) return;

		const latestNotification = myNotifications[0];
		const latestNotificationKey = notificationIdentity(latestNotification);
		const isNewNotification =
			latestNotificationKey != null &&
			!shownNotificationIds.current.has(latestNotificationKey) &&
			!latestNotification?.read?.includes(user._id);

		if (!isNewNotification) return;

		shownNotificationIds.current.add(latestNotificationKey);

		if (isReminderNotification(latestNotification))
		{
			audio.current.src = "/assets/reminder.mp3";
		} else if (latestNotification?.route?.indexOf("project") !== -1)
		{
			audio.current.src = "/assets/discord.mp3";
		} else if (latestNotification?.route?.indexOf("ticket") !== -1)
		{
			audio.current.src = "/assets/meet.mp3";
		}

		audio.current.load();
		audio.current.play().catch((err) => console.warn("Audio play blocked:", err));

		const notificationTitle = localizeLegacyTodoText(
			latestNotification,
			latestNotification?.name?.[lang] ||
				latestNotification?.name?.en ||
				T("notifications", lang),
			lang
		);
		const notificationMessageSource = localizeLegacyTodoText(
			latestNotification,
			latestNotification?.desc?.[lang] ||
				latestNotification?.desc?.en ||
				T("noDescription", lang),
			lang
		);
		const notificationMessage = parse(notificationMessageSource);
		const systemNotificationMessage = notificationMessageSource
			.replace(/<[^>]*>/g, " ")
			.replace(/\s+/g, " ")
			.trim();
		const systemNotificationMessageTrimmed =
			systemNotificationMessage.length > 100
				? `${systemNotificationMessage.slice(0, 100)}...`
				: systemNotificationMessage;
		const autoCloseMs =
			latestNotification?.route?.includes("/chat/chat-group/") ? 2000 : 4000;

		notifications.show({
			title: notificationTitle,
			message: notificationMessage,
			autoClose: autoCloseMs,
			position: "top-right",
			color:
				latestNotification?.importance === "high"
					? "red"
					: latestNotification?.importance === "medium"
						? "orange"
						: "blue",
			onClick: () => {
				openNotificationTarget(latestNotification);
			}
		});

		showSystemNotification({
			title: notificationTitle,
			message: systemNotificationMessageTrimmed,
			notificationKey: latestNotificationKey,
			notification: latestNotification,
			autoCloseMs
		}).catch((error) => {
			console.warn("Failed to show system notification:", error);
		});
		// eslint-disable-next-line
	}, [myNotifications, myNotifications.length, lang, user._id, history]);

	const notificationRows = useMemo(() => {
		return (myNotifications || []).map((notification) => ({
			...notification,
			readByCurrentUser: notification.read?.includes(user._id)
		}));
	}, [myNotifications, user._id]);

	const notificationColumns = useMemo(
		() => [
			{
				accessorKey: "name",
				header: T("notifications", lang),
				enableColumnFilter: false,
				Cell: ({ row }) => (
					<Text fw={row.original.readByCurrentUser ? 500 : 700}>
						{localizeLegacyTodoText(
							row.original,
							row.original.name?.[lang] ||
								row.original.name?.en ||
								T("notifications", lang),
							lang
						)}
					</Text>
				)
			},
			{
				accessorKey: "desc",
				header: T("description", lang),
				enableColumnFilter: false,
				Cell: ({ row }) => (
					<Box style={{ opacity: row.original.readByCurrentUser ? 0.6 : 1 }}>
						{parse(localizeLegacyTodoText(
							row.original,
							row.original?.desc?.[lang] ||
								row.original?.desc?.en ||
								T("noDescription", lang),
							lang
						))}
					</Box>
				)
			},
			{
				id: "createdAt",
				accessorFn: (row) =>
					row.createdAt && moment(row.createdAt).isValid()
						? new Date(row.createdAt)
						: new Date(0),
				header: T("date", lang),
				enableColumnFilter: true,
				filterVariant: "date",
				Cell: ({ row }) => (
					<Text c="dimmed" size="sm">
						{moment(row.original.createdAt).fromNow()}
						{" · "
						}
						{moment(row.original.createdAt).format("HH:mm DD/MM/YYYY")}
					</Text>
				)
			},
			{
				accessorKey: "importance",
				header: T("priority", lang),
				enableColumnFilter: false,
				Cell: ({ row }) => {
					const importance = row.original.importance || "low";
					const color =
						importance === "high" ? "red" : importance === "medium" ? "orange" : "blue";
					const label =
						importance === "high"
							? T("high", lang)
							: importance === "medium"
								? T("medium", lang)
								: T("low", lang);
					return (
						<Badge color={color} variant="light">
							{label}
						</Badge>
					);
				}
			}
		],
		[lang]
	);

	const notificationTable = useMantineReactTable({
		columns: notificationColumns,
		data: notificationRows,
		localization: mrtLocalization,
		enableColumnActions: false,
		enableColumnFilters: true,
		enableDensityToggle: false,
		enableFullScreenToggle: false,
		enableRowSelection: true,
		enableGlobalFilter: true,
		enablePagination: true,
		mantinePaginationProps: {
			showRowsPerPage: !isMobile,
			withEdges: !isMobile,
			rowsPerPageOptions: ["8", "16", "32", "50", "100"]
		},
		mantinePaperProps: {
			className: isMobile ? classes.mobileTable : undefined,
			shadow: "none",
			withBorder: false,
			style: {
				background: "transparent"
			}
		},
		mantineTableContainerProps: {
			style: {
				background: "transparent",
				...(isMobile ? { flex: "1 1 auto", minHeight: 0, minWidth: 0, maxWidth: "100%", overflow: "auto", overscrollBehavior: "contain" } : {})
			}
		},
		mantineTopToolbarProps: {
			style: {
				background: "transparent"
			}
		},
		mantineBottomToolbarProps: {
			style: {
				background: "transparent"
			}
		},
		getRowId: (row, index) => String(row._id ?? row.ID ?? `notification-row-${index}`),
		initialState: {
			pagination: { pageSize: 8, pageIndex: 0 },
			showColumnFilters: true
		},
		renderTopToolbarCustomActions: ({ table }) => (
			<Group spacing="xs" style={{ flexWrap: "wrap" }}>
				<Button
					size="xs"
					variant="light"
					disabled={!(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected())}
					onClick={() => {
						let markedAny = false;
						Object.keys(table.getState().rowSelection).forEach((rowId) => {
							const row = table.getRow(rowId);
							const original = row?.original;
							if (original?._id && !original.read?.includes(user._id))
							{
								emitNotificationRead(original._id);
								markedAny = true;
							}
						});
						if (markedAny) playClearNotificationSound();
						table.toggleAllRowsSelected(false);
					}}
				>
					{T("markSelectedAsRead", lang)}
				</Button>
				<Button
					size="xs"
					variant="filled"
					disabled={unreadCount === 0}
					onClick={() => {
						unreadNotifications.forEach((n) => {
							if (n._id)
							{
								emitNotificationRead(n._id);
							}
						});
						playClearNotificationSound();
						table.toggleAllRowsSelected(false);
					}}
				>
					{T("markAllAsRead", lang)}
				</Button>
			</Group>
		),
		mantineTableBodyRowProps: ({ row }) => ({
			onClick: (event) => {
				if (
					event.target?.closest?.('input[type="checkbox"]') ||
					event.target?.closest?.('[data-mrt-row-select]') ||
					event.target?.closest?.(".mantine-Checkbox-root")
				)
				{
					return;
				}
				const link =
					typeof row.original.route === "string"
						? row.original.route.trim()
						: "";
				if (!link) return;
				OpenClose.close();
				openNotificationTarget(row.original);
			},
			style: {
				cursor:
					typeof row.original.route === "string" && row.original.route.trim()
						? "pointer"
						: "default",
				opacity: row.original.readByCurrentUser ? 0.6 : 1
			}
		})
	});

	return (
		<>
			<audio ref={audio} />
			<audio ref={clearAudio} />
			<Indicator
				disabled={unreadCount === 0}
				label={unreadLabel}
				color="red"
				size={16}
				offset={6}
				position="top-end"
			>
				<Button
					onClick={OpenClose.toggle}
					variant="subtle"
					ml={compact ? 0 : isMobile ? 5 : 10}
					aria-label={T("notificationsBellAria", lang)}
					size={isMobile ? "sm" : "md"}
					style={{
						margin: 0,
						padding: 10,
						position: "relative",
						overflow: "visible",
					}}
				>
					<IconBell size={isMobile ? 18 : 20} stroke={1.5} />
				</Button>
			</Indicator>
			<Modal
				opened={opened}
				onClose={OpenClose.close}
				title={T("notifications", lang)}
				centered={!isMobile}
				fullScreen={isMobile}
				size={isMobile ? undefined : "80%"}
				overlayProps={{
					opacity: 0.55,
					blur: 3
				}}
				scrollAreaComponent={isMobile ? NotificationPanelScroll : ScrollArea.Autosize}
				classNames={isMobile ? { content: classes.mobilePanel, header: classes.mobileHeader, body: classes.mobileBody } : undefined}
				styles={{
					content: {
						background: isMobile ? panelBackground : "transparent",
						...(isMobile ? { height: "100dvh", maxHeight: "100dvh", overflow: "hidden" } : {}),
						minHeight: isMobile ? 0 : 200
					},
					body: {
						paddingLeft: 0,
						paddingRight: 0,
						minHeight: isMobile ? 0 : 200,
						background: isMobile ? panelBackground : "transparent"
					}
				}}
			>
				{myNotifications?.length > 0 ? (
					<MantineReactTable table={notificationTable} />
				) : (
					<Text align="center" size="xl">
						{T("noNotifications", lang)}
					</Text>
				)}
			</Modal>
		</>
	);
}
